"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { useMacro, usePeg, type PegStable } from "@/lib/useIntel";

// The "what's moving the dollar" strip: USDT/USDC peg badges + prediction-market odds on
// dollar-moving events (Fed, CPI, …). Renders NOTHING when the API has no data. The strip
// is additive, and an empty ribbon would just be noise.

const PegBadge = ({ s }: { s: PegStable }) => {
  const tone =
    s.status === "ok"
      ? "text-buy border-buy/30 bg-buy/10"
      : s.status === "warn"
        ? "text-gold border-gold/40 bg-gold/10"
        : "text-sell border-sell/40 bg-sell/10";
  const label =
    s.status === "ok" ? "on peg" : s.status === "warn" ? "drifting" : "DEPEG";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${tone}`}
      title={`${s.symbol} $${s.price} (${s.deviation_bps > 0 ? "+" : ""}${s.deviation_bps} bps from $1)`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {s.symbol} {label}
    </span>
  );
};

const IntelStrip = () => {
  const { data: peg } = usePeg();
  const { data: macro } = useMacro();
  const ref = useRef<HTMLDivElement>(null);

  const stables = peg?.stables ?? [];
  const items = macro?.items ?? [];
  const hasContent = stables.length > 0 || items.length > 0;

  useEffect(() => {
    if (!hasContent || !ref.current) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      // Entrance only, no color tweens here. (Colors + GSAP burned us once: GSAP cannot
      // parse hsl(var(--…)); see tokenHsla in lib/theme.)
      gsap.from(ref.current, { opacity: 0, y: -6, duration: 0.4, ease: "power2.out" });
    });
    return () => mm.revert();
  }, [hasContent]);

  if (!hasContent) return null;

  return (
    <div
      ref={ref}
      className="flex items-center gap-2 overflow-x-auto rounded-lg border border-line bg-surface px-3 py-2 [scrollbar-width:none]"
      aria-label="Market intelligence"
    >
      {stables.map((s) => (
        <PegBadge key={s.symbol} s={s} />
      ))}

      {stables.length > 0 && items.length > 0 && (
        <span className="h-4 w-px shrink-0 bg-line" aria-hidden="true" />
      )}

      {items.map((m) => (
        <span
          key={m.id}
          className="inline-flex shrink-0 items-baseline gap-1.5 text-[11px] text-fg-muted"
          title={`${m.question}, ${m.venue}`}
        >
          <span className="text-fg-secondary">{m.label}:</span>
          <span className="tnum text-fg">{Math.round(m.odds * 100)}%</span>
          <span className="max-w-[180px] truncate sm:max-w-[260px]">{m.question}</span>
        </span>
      ))}
    </div>
  );
};

export default IntelStrip;
